/**
 * Placeholder for the window where a SafeContext collection has been emptied
 * but not yet refetched.
 *
 * `lockVault()` clears `items` and `orgs`, and `unlock()` flips status to
 * 'unlocked' before either refetch resolves — so for one or more round-trips an
 * empty array means "still loading", not "doesn't exist". Detail pages that
 * `.find()` into those arrays used to read that as absence and render
 * "Item not found." / "Family not found." until the data landed.
 *
 * Pair this with the matching `itemsLoaded` / `orgsLoaded` flag from
 * `useSafe()`, never with a bare emptiness check:
 *
 *   if (!item) return itemsLoaded ? <NotFound/> : <LoadingPane label="Loading item…" />;
 */
interface LoadingPaneProps {
  /** Sentence-case, with an ellipsis — e.g. "Loading family…". */
  label: string;
  /**
   * Container classes. Defaults to the full-height centred shape the detail
   * pages use for their not-found state, so swapping between the two causes no
   * layout shift. Pass the host's own spacing when it renders inline instead.
   */
  className?: string;
}

const DEFAULT_CLASS = 'h-full flex flex-col items-center justify-center text-hbs-text-mute gap-3';

export default function LoadingPane({ label, className }: LoadingPaneProps): JSX.Element {
  return (
    <div role="status" aria-live="polite" className={className ?? DEFAULT_CLASS}>
      <p className="text-[14px]">{label}</p>
    </div>
  );
}
